import { useEffect } from 'react';

const PROD_ORIGIN = 'https://smt-services17.fr';
const DEFAULT_OG_IMAGE = `${PROD_ORIGIN}/logo-smt.png`;

/** Set or update a meta tag by attribute (name or property). */
const upsertMeta = (attr, key, value) => {
  if (!value) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
};

/** Set or update <link rel="canonical" />. */
const upsertCanonical = (href) => {
  if (!href) return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

/** Normalize a path for canonical URLs: strip trailing slash (except root), drop querystring/hash. */
const normalizePath = (p) => {
  if (!p) return '/';
  let path = p.split('?')[0].split('#')[0];
  if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);
  return path || '/';
};

/**
 * Update document title + meta tags (description, OG, Twitter, canonical, robots)
 * for SPA route changes. Optionally attaches a page-scoped JSON-LD script.
 *
 * @param {Object} cfg
 * @param {string} cfg.title           Full <title> content.
 * @param {string} cfg.description     Meta description (~150-160 chars).
 * @param {string} [cfg.path]          Path for canonical/OG URL (e.g. "/blog"). Defaults to current.
 * @param {string} [cfg.image]         Absolute URL of the OG/Twitter image.
 * @param {'website'|'article'} [cfg.ogType]
 * @param {boolean} [cfg.noindex]      Set robots="noindex,nofollow".
 * @param {object|null} [cfg.jsonLd]   JSON-LD structured data for this page. Removed on unmount.
 */
export const useDocumentMeta = ({
  title,
  description,
  path,
  image = DEFAULT_OG_IMAGE,
  ogType = 'website',
  noindex = false,
  jsonLd = null,
}) => {
  useEffect(() => {
    if (title) document.title = title;

    if (description) {
      upsertMeta('name', 'description', description);
      upsertMeta('property', 'og:description', description);
      upsertMeta('name', 'twitter:description', description);
    }
    if (title) {
      upsertMeta('property', 'og:title', title);
      upsertMeta('name', 'twitter:title', title);
    }

    const rawPath = path || (typeof window !== 'undefined' ? window.location.pathname : '/');
    const url = `${PROD_ORIGIN}${normalizePath(rawPath)}`;
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:type', ogType);
    upsertCanonical(url);

    upsertMeta('property', 'og:image', image);
    upsertMeta('name', 'twitter:image', image);
    upsertMeta('name', 'twitter:card', 'summary_large_image');

    upsertMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow');

    // Page-scoped JSON-LD (distinct from the global LocalBusiness in index.html).
    const SCRIPT_ID = 'page-jsonld';
    const existing = document.getElementById(SCRIPT_ID);
    if (jsonLd) {
      const payload = JSON.stringify(jsonLd);
      if (existing) {
        existing.textContent = payload;
      } else {
        const s = document.createElement('script');
        s.id = SCRIPT_ID;
        s.type = 'application/ld+json';
        s.textContent = payload;
        document.head.appendChild(s);
      }
    } else if (existing) {
      existing.remove();
    }
    return () => {
      const el = document.getElementById(SCRIPT_ID);
      if (el) el.remove();
    };
  }, [title, description, path, image, ogType, noindex, jsonLd]);
};
